import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, IconButton, Button,
    Avatar, Divider, CircularProgress, Grow, Dialog,
    DialogTitle, DialogContent, DialogActions, Tooltip, Stack, Popover,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip,
    Select, MenuItem
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import GridOnIcon from '@mui/icons-material/GridOn';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableChartIcon from '@mui/icons-material/TableChart';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';
import { marked } from 'marked';
import API_URL from '../../config';
import Loading from '../../components/Loading';

const TYPE_META = {
    pdf: { label: 'PDF', color: '#e53935', icon: <PictureAsPdfIcon sx={{ fontSize: 14 }} /> },
    word: { label: 'Word', color: '#1565c0', icon: <DescriptionIcon sx={{ fontSize: 14 }} /> },
    map_pdf: { label: 'Map', color: '#2e7d32', icon: <MapIcon sx={{ fontSize: 14 }} /> },
    excel: { label: 'Excel', color: '#00695c', icon: <GridOnIcon sx={{ fontSize: 14 }} /> },
};

const WORK_STATUSES = ['New', 'In Progress', 'Completed', 'Cancelled'];

const WORK_STATUS_COLOR = {
    'New': '#90caf9',
    'In Progress': '#ffcc80',
    'Completed': '#a5d6a7',
    'Cancelled': '#ef9a9a',
};

function TenderingChat() {
    const { guid } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([{ sender: 'bot', text: 'Hello! Ask me anything about the uploaded tender documents.' }]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [fileFilter, setFileFilter] = useState('all');
    const [filesAnchorEl, setFilesAnchorEl] = useState(null);
    const [popoverFiles, setPopoverFiles] = useState([]);
    const [boqOpen, setBoqOpen] = useState(false);
    const [boqRows, setBoqRows] = useState([]);
    const [boqColumns, setBoqColumns] = useState([]);
    const [boqMsgIndex, setBoqMsgIndex] = useState(null);
    const [boqTemplateType, setBoqTemplateType] = useState(null);
    const [boqSaving, setBoqSaving] = useState(false);
    const [boqExporting, setBoqExporting] = useState(false);
    const chatEndRef = useRef(null);
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    useEffect(() => {
        const load = async () => {
            try {
                const [sesRes, msgRes] = await Promise.all([
                    axios.get(`${API_URL}/tendering/sessions/${guid}`, { headers }),
                    axios.get(`${API_URL}/tendering/sessions/${guid}/messages`, { headers })
                ]);
                setSession(sesRes.data);

                const history = Array.isArray(msgRes.data) ? msgRes.data : [];
                if (history.length > 0) {
                    setMessages(history.map(m => ({
                        sender: m.role === 'user' ? 'user' : 'bot',
                        text: m.message || '',
                        boqTable: m.boq?.rows || [],
                        boqColumns: m.boq?.columns || [],
                        boqId: m.boq?.id || null,
                        templateType: m.template_type || m.boq?.template_type || null,
                        hasBoq: Boolean(m.has_boq) && Boolean(m.boq)
                    })));
                }
            } catch (err) {
                console.error('Error loading tendering session:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [guid]);

    useEffect(() => {
        if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages, chatLoading]);

    const handleSendMessage = async () => {
        if (!chatInput.trim() || chatLoading) return;
        const userMessage = chatInput.trim();
        setMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
        setChatInput('');
        setChatLoading(true);
        setMessages(prev => [...prev, { sender: 'bot', text: 'Thinking...', isLoader: true }]);
        try {
            const res = await axios.post(`${API_URL}/tendering/chat`,
                { session_id: guid, message: userMessage },
                { headers: { ...headers, 'Content-Type': 'application/json' }, timeout: 600000 }
            );
            const data = res.data || {};
            // Prefer the server-normalized rows/columns (handles every BOQ shape)
            const boqRowsResp = Array.isArray(data.boq_rows) ? data.boq_rows : [];
            const boqColsResp = Array.isArray(data.boq_columns) ? data.boq_columns : [];
            const rawBoq = data.boq_table;
            const fallback = normalizeBoq(rawBoq);
            const finalRows = boqRowsResp.length > 0 ? boqRowsResp : fallback.rows;
            const finalCols = boqColsResp.length > 0 ? boqColsResp : fallback.columns;
            setMessages(prev => {
                const base = [...prev];
                base[base.length - 1] = {
                    sender: 'bot',
                    text: data.message || data.error || 'No response.',
                    boqTable: finalRows,
                    boqColumns: finalCols,
                    boqId: data.boq_id || null,
                    templateType: data.template_type || null,
                    hasBoq: finalRows.length > 0
                };
                return base;
            });
        } catch (error) {
            console.error('Chat error:', error);
            const msg = error.response?.data?.error || error.message || 'Sorry, there was an error processing your request.';
            setMessages(prev => { const base = [...prev]; base[base.length - 1] = { sender: 'bot', text: msg }; return base; });
        } finally {
            setChatLoading(false);
        }
    };

    const fileTypeMeta = (ftype) => {
        const m = TYPE_META[ftype];
        return m || { label: 'File', color: '#64748b', icon: <DescriptionIcon sx={{ fontSize: 14 }} /> };
    };

    const closePreview = () => {
        setPreviewFile(null);
        if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    };

    const fetchFileBlob = async (file) => {
        const res = await axios.get(`${API_URL}/tendering/sessions/${guid}/files/${file.id}/download`, {
            headers,
            responseType: 'blob'
        });
        return res.data;
    };

    const handlePreview = async (file) => {
        if (file.file_type === 'word' || file.file_type === 'excel') {
            alert('Preview is available for PDF files only. Please use Download for Word/Excel files.');
            return;
        }
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setPreviewFile(file);
        } catch (err) {
            alert('Could not load preview: ' + (err.message || err));
        }
    };

    const handleDownload = async (file) => {
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.file_name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            alert('Download failed: ' + (err.message || err));
        }
    };

    const filteredFiles = fileFilter === 'all'
        ? (session?.files || [])
        : (session?.files || []).filter(f => f.file_type === fileFilter);

    const openFilesPopover = (event, type) => {
        const list = type === 'all' ? (session?.files || []) : (session?.files || []).filter(f => f.file_type === type);
        setPopoverFiles(list);
        setFileFilter(type);
        setFilesAnchorEl(event.currentTarget);
    };

    const closeFilesPopover = () => setFilesAnchorEl(null);

    const handleWorkStatusChange = async (newStatus) => {
        const prev = session?.work_status;
        setSession(s => ({ ...s, work_status: newStatus }));
        try {
            await axios.put(`${API_URL}/tendering/sessions/${guid}/work-status`,
                { work_status: newStatus },
                { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
        } catch (err) {
            console.error('Update status error:', err);
            setSession(s => ({ ...s, work_status: prev }));
            alert('Could not update status: ' + (err.response?.data?.error || err.message));
        }
    };

    // ---- BOQ table (editable popup) ----
    const normalizeBoq = (table) => {
        if (!table) return { columns: [], rows: [] };

        // Object wrappers: { headers/columns, rows/data } or { col: [values] }
        if (!Array.isArray(table) && typeof table === 'object') {
            const rowsPart = table.rows || table.data;
            const headersPart = table.headers || table.columns;
            if (Array.isArray(rowsPart)) {
                const inner = normalizeBoq(rowsPart);
                if (Array.isArray(headersPart) && headersPart.length > 0) {
                    const cols = headersPart.map(h => String(h));
                    return { columns: cols, rows: inner.rows.map(r => { const o = {}; cols.forEach(c => { o[c] = r[c] ?? ''; }); return o; }) };
                }
                return inner;
            }
            const listKeys = Object.keys(table).filter(k => Array.isArray(table[k]));
            if (listKeys.length > 0) {
                const len = Math.max(...listKeys.map(k => table[k].length));
                const rows = [];
                for (let i = 0; i < len; i++) {
                    const o = {};
                    listKeys.forEach(k => { const v = table[k][i]; o[k] = v === null || v === undefined ? '' : String(v); });
                    rows.push(o);
                }
                return { columns: listKeys, rows };
            }
            const cols = Object.keys(table);
            const o = {};
            cols.forEach(c => { o[c] = table[c] === null || table[c] === undefined ? '' : String(table[c]); });
            return { columns: cols, rows: [o] };
        }

        if (!Array.isArray(table) || table.length === 0) return { columns: [], rows: [] };
        // Case 1: array of objects
        if (typeof table[0] === 'object' && !Array.isArray(table[0]) && table[0] !== null) {
            const cols = [];
            table.forEach(r => Object.keys(r || {}).forEach(k => { if (!cols.includes(k)) cols.push(k); }));
            const rows = table.map(r => {
                const obj = {};
                cols.forEach(c => { obj[c] = r?.[c] === null || r?.[c] === undefined ? '' : String(r[c]); });
                return obj;
            });
            return { columns: cols, rows };
        }
        // Case 2: array of arrays -> first row is the header
        if (Array.isArray(table[0])) {
            const cols = table[0].map((c, i) => (c === null || c === undefined || String(c).trim() === '') ? `Column ${i + 1}` : String(c));
            const rows = table.slice(1).map(arr => {
                const obj = {};
                cols.forEach((c, i) => { obj[c] = arr?.[i] === null || arr?.[i] === undefined ? '' : String(arr[i]); });
                return obj;
            });
            return { columns: cols, rows };
        }
        // Case 3: array of primitives
        return { columns: ['Value'], rows: table.map(v => ({ Value: String(v) })) };
    };

    const openBoq = (msgIndex) => {
        const msg = messages[msgIndex];
        if (!msg) return;
        if (Array.isArray(msg.boqColumns) && msg.boqColumns.length > 0) {
            const cols = msg.boqColumns;
            const rows = (msg.boqTable || []).map(r => {
                const obj = {};
                cols.forEach(c => { obj[c] = r?.[c] === null || r?.[c] === undefined ? '' : String(r[c]); });
                return obj;
            });
            setBoqColumns(cols);
            setBoqRows(rows);
        } else {
            const { columns, rows } = normalizeBoq(msg.boqTable);
            setBoqColumns(columns);
            setBoqRows(rows);
        }
        setBoqTemplateType(msg.templateType || null);
        setBoqMsgIndex(msgIndex);
        setBoqOpen(true);
    };

    const closeBoq = () => {
        setBoqOpen(false);
        setBoqMsgIndex(null);
    };

    const handleBoqCellChange = (rowIdx, col, value) => {
        setBoqRows(prev => {
            const next = [...prev];
            next[rowIdx] = { ...next[rowIdx], [col]: value };
            return next;
        });
    };

    const addBoqRow = () => {
        const blank = {};
        boqColumns.forEach(c => { blank[c] = ''; });
        setBoqRows(prev => [...prev, blank]);
    };

    const deleteBoqRow = (rowIdx) => {
        setBoqRows(prev => prev.filter((_, i) => i !== rowIdx));
    };

    const saveBoq = async () => {
        if (boqMsgIndex === null) return;
        const msg = messages[boqMsgIndex];
        const boqId = msg?.boqId;

        // Persist to SQL when the BOQ has a saved record
        if (boqId) {
            setBoqSaving(true);
            try {
                await axios.put(`${API_URL}/tendering/boq/${boqId}`,
                    { columns: boqColumns, rows: boqRows },
                    { headers: { ...headers, 'Content-Type': 'application/json' } }
                );
            } catch (err) {
                console.error('Save BOQ error:', err);
                alert('Could not save BOQ: ' + (err.response?.data?.error || err.message));
                setBoqSaving(false);
                return;
            } finally {
                setBoqSaving(false);
            }
        }

        setMessages(prev => {
            const base = [...prev];
            if (base[boqMsgIndex]) base[boqMsgIndex] = { ...base[boqMsgIndex], boqTable: boqRows, boqColumns };
            return base;
        });
        closeBoq();
    };

    const exportBoq = async () => {
        if (boqColumns.length === 0) return;
        setBoqExporting(true);
        try {
            const res = await axios.post(`${API_URL}/tendering/export_excel`,
                {
                    session_id: guid,
                    template_type: boqTemplateType || 'dynamic',
                    headers: boqColumns,
                    rows: boqRows.map(r => {
                        const obj = {};
                        boqColumns.forEach(c => { obj[c] = r[c] ?? ''; });
                        return obj;
                    })
                },
                { headers, responseType: 'blob', timeout: 300000 }
            );

            // The API returns JSON (not a file) when generation fails
            const ctype = res.headers?.['content-type'] || '';
            if (ctype.includes('application/json')) {
                const text = await res.data.text();
                let msg = text;
                try { msg = JSON.parse(text).message || JSON.parse(text).error || text; } catch (e) {}
                alert('Export failed: ' + msg);
                return;
            }

            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'BOQ_Schedule.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            console.error('Export error:', err);
            let msg = err.response?.data?.error || err.message;
            if (err.response?.data instanceof Blob) {
                try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (e) {}
            }
            alert('Export failed: ' + msg);
        } finally {
            setBoqExporting(false);
        }
    };

    if (loading) return <Loading />;

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
                <Paper elevation={0} sx={{ mb: 4, mt: 2, p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/tendering')}
                            sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                        >
                            Back to Tendering
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                            <Avatar sx={{ width: 52, height: 52, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.15)', color: '#e3f2fd', boxShadow: '0 6px 18px rgba(0,0,0,0.2)', border: '2px solid rgba(255,255,255,0.2)' }}>
                                <SmartToyIcon sx={{ color: 'white', fontSize: 26 }} />
                            </Avatar>
                            <Typography variant="h5" fontWeight="900" sx={{ letterSpacing: 0.3, wordBreak: 'break-word', color: 'white', lineHeight: 1.3 }}>
                                {session?.name || 'Tender AI Chat'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ml: 0.5, mt: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', px: 1, py: 0.3, borderRadius: 3, border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: WORK_STATUS_COLOR[session?.work_status || 'New'], boxShadow: `0 0 6px ${WORK_STATUS_COLOR[session?.work_status || 'New']}` }} />
                                <Select
                                    value={session?.work_status || 'New'}
                                    onChange={(e) => handleWorkStatusChange(e.target.value)}
                                    variant="standard"
                                    disableUnderline
                                    sx={{
                                        color: 'white', fontWeight: 700, fontSize: '0.75rem', letterSpacing: 0.3,
                                        '& .MuiSelect-select': { py: 0.4, pr: '20px !important' },
                                        '& .MuiSvgIcon-root': { color: 'white', fontSize: 18 }
                                    }}
                                >
                                    {WORK_STATUSES.map(st => (
                                        <MenuItem key={st} value={st} sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{st}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', px: 1.8, py: 0.7, borderRadius: 3, border: '1px solid rgba(255,255,255,0.12)' }}>
                                <SmartToyIcon sx={{ color: '#93c5fd', fontSize: 14 }} />
                                <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>{guid}</Typography>
                            </Box>
                            {session?.country && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', px: 1.8, py: 0.7, borderRadius: 3, border: '1px solid rgba(255,255,255,0.12)' }}>
                                    <Typography variant="caption" sx={{ color: '#86efac', fontWeight: 600, fontSize: '0.75rem' }}>{session.country}</Typography>
                                </Box>
                            )}
                            {session?.division && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', px: 1.8, py: 0.7, borderRadius: 3, border: '1px solid rgba(255,255,255,0.12)' }}>
                                    <Typography variant="caption" sx={{ color: '#fde68a', fontWeight: 600, fontSize: '0.75rem' }}>{session.division}</Typography>
                                </Box>
                            )}
                            <Button
                                onClick={(e) => openFilesPopover(e, 'all')}
                                sx={{ minWidth: 0, textTransform: 'none', px: 1.8, py: 0.5, borderRadius: 3, fontWeight: 700, fontSize: '0.75rem', color: fileFilter === 'all' ? '#1e3c72' : 'white', bgcolor: fileFilter === 'all' ? 'white' : 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', '&:hover': { bgcolor: fileFilter === 'all' ? 'white' : 'rgba(255,255,255,0.25)' } }}
                            >
                                All ({session?.file_count || 0})
                            </Button>
                            {Object.entries(TYPE_META).map(([k, m]) => {
                                const count = session?.file_types ? session.file_types[k] : 0;
                                if (!count) return null;
                                return (
                                    <Button
                                        key={k}
                                        onClick={(e) => openFilesPopover(e, k)}
                                        sx={{ minWidth: 0, textTransform: 'none', px: 1.8, py: 0.5, borderRadius: 3, fontWeight: 700, fontSize: '0.75rem', color: fileFilter === k ? '#1e3c72' : 'white', bgcolor: fileFilter === k ? 'white' : 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', '&:hover': { bgcolor: fileFilter === k ? 'white' : 'rgba(255,255,255,0.25)' } }}
                                    >
                                        {m.label} ({count})
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                </Paper>
            </Grow>

            <Paper elevation={0} sx={{ height: 'calc(100vh - 240px)', minHeight: 420, border: '1px solid rgba(0,0,0,0.04)', borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#fafbfc' }}>
                    {messages.map((m, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start', mb: 1.5 }}>
                            {m.sender !== 'user' && (
                                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1565c0', width: 32, height: 32, mr: 1 }}>
                                    <SmartToyIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                            )}
                            <Box sx={{ maxWidth: '75%' }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: m.sender === 'user' ? '#e3f2fd' : '#ffffff',
                                        color: '#1e293b',
                                        border: m.sender === 'user' ? '1px solid #bbdefb' : '1px solid #e2e8f0',
                                        whiteSpace: m.sender === 'user' ? 'pre-wrap' : 'normal',
                                        wordBreak: 'break-word',
                                        overflowX: 'auto'
                                    }}
                                >
                                    {m.isLoader ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={14} sx={{ color: '#1565c0' }} />
                                            <Typography variant="body2">{m.text}</Typography>
                                        </Box>
                                    ) : m.sender === 'user' ? (
                                        <Typography variant="body2">{m.text}</Typography>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            sx={{
                                                lineHeight: 1.6,
                                                '& p': { m: 0, mb: 1, '&:last-of-type': { mb: 0 } },
                                                '& strong, & b': { fontWeight: 800, color: '#0f172a' },
                                                '& em': { fontStyle: 'italic' },
                                                '& h1, & h2, & h3, & h4': { fontSize: '0.95rem', fontWeight: 800, m: 0, mt: 1.5, mb: 0.75, color: '#0f172a' },
                                                '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
                                                '& li': { mb: 0.5 },
                                                '& pre': { bgcolor: '#f1f5f9', p: 1, borderRadius: 1, overflowX: 'auto', my: 1, fontSize: '0.75rem' },
                                                '& code': { fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 0.5, borderRadius: 0.5, fontSize: '0.78rem' },
                                                '& a': { color: '#1976d2' },
                                                '& hr': { border: 'none', borderTop: '1px solid #e2e8f0', my: 1 },
                                                '& table': { borderCollapse: 'collapse', width: '100%', my: 1, fontSize: '0.75rem' },
                                                '& th, & td': { border: '1px solid #e2e8f0', px: 0.75, py: 0.5, textAlign: 'left' },
                                                '& th': { bgcolor: '#f8fafc', fontWeight: 700 },
                                                '& blockquote': { m: 0, my: 1, pl: 1.5, borderLeft: '3px solid #cbd5e1', color: '#475569' }
                                            }}
                                            dangerouslySetInnerHTML={{ __html: marked.parse(m.text || '') }}
                                        />
                                    )}
                                    {m.hasBoq && !m.isLoader && (
                                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<TableChartIcon />}
                                                onClick={() => openBoq(i)}
                                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)' }}
                                            >
                                                View BOQ Table ({Array.isArray(m.boqTable) ? m.boqTable.length : 0})
                                            </Button>
                                            {m.templateType && (
                                                <Chip size="small" label={`${m.templateType} template`} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }} />
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                            {m.sender === 'user' && (
                                <Avatar sx={{ bgcolor: '#1976d2', color: '#fff', width: 32, height: 32, ml: 1 }}>
                                    <PersonIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                            )}
                        </Box>
                    ))}
                    <div ref={chatEndRef} />
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffffff' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Type your question about the tender documents..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={chatLoading}
                        sx={{ '& .MuiInputBase-input': { color: '#1e293b' } }}
                    />
                    <IconButton color="primary" onClick={handleSendMessage} disabled={chatLoading || !chatInput.trim()} sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' } }}>
                        <SendIcon />
                    </IconButton>
                </Box>
            </Paper>

            <Popover
                open={Boolean(filesAnchorEl)}
                anchorEl={filesAnchorEl}
                onClose={closeFilesPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { borderRadius: 3, mt: 1, width: 360 } }}
            >
                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {fileFilter === 'all' ? 'Files' : `${TYPE_META[fileFilter]?.label || 'File'} Files`} ({popoverFiles.length})
                    </Typography>
                </Box>
                <Box sx={{ maxHeight: 360, overflowY: 'auto', p: 1 }}>
                    {popoverFiles.length > 0 ? (
                        <Stack spacing={1}>
                            {popoverFiles.map((f) => {
                                const meta = fileTypeMeta(f.file_type);
                                return (
                                    <Paper key={f.id} elevation={0} sx={{ p: 1, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafbfc' }}>
                                        <Avatar sx={{ bgcolor: '#f1f5f9', color: meta.color, width: 30, height: 30 }}>{meta.icon}</Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap title={f.file_name}>{f.file_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{meta.label}</Typography>
                                        </Box>
                                        <Tooltip title="Preview">
                                            <IconButton size="small" onClick={() => handlePreview(f)}><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download">
                                            <IconButton size="small" onClick={() => handleDownload(f)}><DownloadIcon sx={{ fontSize: 18 }} /></IconButton>
                                        </Tooltip>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No files.</Typography>
                    )}
                </Box>
            </Popover>

            <Dialog open={boqOpen} onClose={closeBoq} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableChartIcon />
                        BOQ Table {boqTemplateType ? `(${boqTemplateType} template)` : ''}
                    </Box>
                    <CloseIcon onClick={closeBoq} sx={{ cursor: 'pointer' }} />
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                    {boqColumns.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>No BOQ data.</Typography>
                    ) : (
                        <TableContainer sx={{ maxHeight: '68vh' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem', bgcolor: '#f1f5f9', width: 48 }}>#</TableCell>
                                        {boqColumns.map((c) => (
                                            <TableCell key={c} sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem', bgcolor: '#f1f5f9', whiteSpace: 'nowrap' }}>{c}</TableCell>
                                        ))}
                                        <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem', bgcolor: '#f1f5f9', width: 60 }}>DEL</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {boqRows.map((row, rowIdx) => (
                                        <TableRow key={rowIdx} hover>
                                            <TableCell sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>{rowIdx + 1}</TableCell>
                                            {boqColumns.map((c) => (
                                                <TableCell key={c} sx={{ p: 0.5 }}>
                                                    <TextField
                                                        value={row[c] ?? ''}
                                                        onChange={(e) => handleBoqCellChange(rowIdx, c, e.target.value)}
                                                        variant="standard"
                                                        fullWidth
                                                        multiline
                                                        InputProps={{ disableUnderline: true, sx: { fontSize: '0.8rem', px: 0.5 } }}
                                                        sx={{ minWidth: 120, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell align="center">
                                                <Tooltip title="Delete row">
                                                    <IconButton size="small" onClick={() => deleteBoqRow(rowIdx)}><DeleteIcon sx={{ fontSize: 18, color: '#e53935' }} /></IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {boqRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={boqColumns.length + 2} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">No rows. Use "Add Row" to start.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>{boqRows.length} row(s)</Typography>
                    <Button startIcon={<AddIcon />} onClick={addBoqRow} disabled={boqColumns.length === 0} sx={{ fontWeight: 'bold', textTransform: 'none' }}>Add Row</Button>
                    <Button startIcon={boqExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />} onClick={exportBoq} disabled={boqColumns.length === 0 || boqExporting} sx={{ fontWeight: 'bold', textTransform: 'none' }}>{boqExporting ? 'Exporting...' : 'Export Excel'}</Button>
                    <Button onClick={closeBoq} sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        startIcon={boqSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={saveBoq}
                        disabled={boqSaving}
                        sx={{ borderRadius: 2, px: 3, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)' }}
                    >
                        {boqSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(previewFile)} onClose={closePreview} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {previewFile?.file_name || 'Preview'}
                    <CloseIcon onClick={closePreview} sx={{ cursor: 'pointer' }} />
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#525659', height: '78vh' }}>
                    {previewUrl ? <iframe src={previewUrl} title="preview" style={{ width: '100%', height: '100%', border: 'none' }} /> : null}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default TenderingChat;
